import { useEffect, useState } from "react";
import { useScrollToError } from "../hooks/useScrollToError.ts";
import { useStableValidateMessage } from "../hooks/useStableValidateMessage.ts";
import "../knowledge-radios.css";
import { getContainerClasses } from "../utils/inputStyles";
import CheckboxItem from "./CheckboxItem";
import FieldValidationMessage from "./FieldValidationMessage.tsx";

interface DataSourceItem {
  key: string;
  text: string;
}

interface IProps {
  label: string;
  name: string;
  disabled?: boolean;
  options: DataSourceItem[];
  value: string[];
  updateFieldValue?: (value: string[]) => void;
  triggerFieldChange?: (value: string[]) => void;
  onToggle?: (key: string, checked: boolean) => void;
  validatemessage?: string;
}

function CheckboxGroup(props: IProps) {
  const [selectedValues, setSelectedValues] = useState<string[]>(props.value);
  const isDisabled = !!props.disabled;
  const validateMessage = useStableValidateMessage(props.validatemessage);
  const hasError = !!validateMessage;
  const rootRef = useScrollToError(validateMessage, [
    props.value,
    props.updateFieldValue,
    props.onToggle,
  ]);

  useEffect(() => {
    setSelectedValues(props.value);
  }, [props.value]);

  const handleChange = (key: string, checked: boolean) => {
    if (isDisabled) return;

    const newValues = checked
      ? selectedValues.includes(key)
        ? selectedValues
        : [...selectedValues, key]
      : selectedValues.filter((v) => v !== key);

    setSelectedValues(newValues);

    if (props.onToggle) {
      props.onToggle(key, checked);
      return;
    }

    props.updateFieldValue?.(newValues);
    props.triggerFieldChange?.(newValues);
  };

  const containerClasses = getContainerClasses({});
  const options = Array.isArray(props.options) ? props.options : [];
  const errorClass = hasError ? "kr-radio-error" : "";

  return (
    <div ref={rootRef} className={containerClasses}>
      {props.label && <label>{props.label}</label>}
      <div
        className={`radio-list ${errorClass} ${isDisabled ? "radio-list-disabled" : ""} w-full`}
      >
        {options.map((option) => (
          <CheckboxItem
            key={option.key}
            id={`${props.name}-${option.key}`}
            checked={selectedValues.includes(option.key)}
            caption={option.text}
            disabled={isDisabled}
            hasError={hasError}
            variant="tile"
            onChange={(checked) => handleChange(option.key, checked)}
          />
        ))}
      </div>
      <FieldValidationMessage message={validateMessage} />
    </div>
  );
}

export default CheckboxGroup;
